import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import './SearchAllocation.css';

const SearchAllocation = ({ mode = 'View', onSelect = null }) => {
  const navigate = useNavigate();
  const [allocations, setAllocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedAllocationId, setSelectedAllocationId] = useState(null);

  // Define table columns
  const columns = [
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value }) => <span className="pg-name">{value}</span>
    },
    {
      Header: 'Mobile',
      accessor: 'pg_mobile'
    },
    {
      Header: 'Building',
      accessor: 'building_name'
    },
    {
      Header: 'Room',
      accessor: 'room_name'
    },
    {
      Header: 'Rent Amount',
      accessor: 'rent_amount',
      Cell: ({ value }) => `₹${value}`
    },
    {
      Header: 'Start Date',
      accessor: 'start_date',
      Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={`status-badge status-${value.toLowerCase()}`}>
          {value}
        </span>
      )
    },
    {
      Header: 'Action',
      accessor: 'allocation_id',
      Cell: ({ value, row }) => (
        mode === 'Select' ? (
          <input
            type="radio"
            name="selectedAllocation"
            value={value}
            checked={selectedAllocationId === value}
            onChange={(e) => setSelectedAllocationId(parseInt(e.target.value))}
          />
        ) : (
          <RTButton
            variant="outline"
            size="small"
            onClick={() => handleViewAllocation(value)}
          >
            View
          </RTButton>
        )
      )
    }
  ];

  const searchAllocations = async () => {
    setSearchLoading(true);
    try {
      const response = await apiClient.get(`/allocation/search?search_text=${searchTerm}&status=Allocated`);
      setAllocations(response.data || []);
      if (response.data && response.data.length === 0) {
        if (searchTerm.trim()) {
          toastError('No active allocations found for the search criteria');
        } else {
          toastError('No active allocations found');
        }
      }
    } catch (error) {
      console.error('Error searching allocations:', error);
      toastError('Failed to search allocations');
      setAllocations([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectAllocation = () => {
    const selectedAllocation = allocations.find(
      allocation => allocation.allocation_id === selectedAllocationId
    );
    
    if (selectedAllocation && onSelect) {
      onSelect(selectedAllocation);
      toastSuccess('Allocation selected successfully');
    } else {
      toastError('Please select an allocation');
    }
  };

  const handleViewAllocation = (allocationId) => {
    navigate(`/allocation/${allocationId}`);
  };

  // Remove automatic loading - only load when search button is pressed

  return (
    <div className="search-allocation">
      <div className="search-section">
        <h3>Search Allocations</h3>
        <div className="search-controls">
          <div className="search-input-group">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchAllocations();
                }
              }}
              placeholder="Enter PG name, mobile number, or building name (optional)"
              className="search-input"
            />
            <RTButton 
              variant="primary" 
              onClick={searchAllocations}
              disabled={searchLoading}
              style={{ width: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {searchLoading ? (
                <RTSpinner size="small" showMessage={false} />
              ) : (
                'Search'
              )}
            </RTButton>
          </div>
          <p className="search-hint">Leave empty and click Search to load all active allocations</p>
        </div>
      </div>

      {allocations.length > 0 && (
        <div className="allocations-table-section">
          <Table
            columns={columns}
            data={allocations}
            title={`Allocations (${allocations.length})`}
            showSearch={false}
            exportable={true}
            showPagination={true}
            noDataText="No allocations found"
            pageSize={10}
          />

          {mode === 'Select' && (
            <div className="selection-actions">
              <RTButton
                variant="primary"
                onClick={handleSelectAllocation}
                disabled={!selectedAllocationId}
              >
                Select Allocation
              </RTButton>
            </div>
          )}
        </div>
      )}

      {searchLoading && (
        <div className="loading-container">
          <RTSpinner />
          <p>Loading allocations...</p>
        </div>
      )}
    </div>
  );
};

export default SearchAllocation;
